import type { AdminTarget } from '@dunea/plugin-sdk';
import type { AdminExtensionSessionRequest, AdminExtensionView } from '@dunea/shared';
import httpClient from '~/lib/http-client';

// Admin extensions REST client (contract: packages/shared/src/marketplace/contracts.ts).
// `appId` is the chat id.

const seg = encodeURIComponent;
const apps = (appId: string) => `/api/marketplace/apps/${seg(appId)}`;

/** Installed plugins' extensions for one target. */
export async function apiListAdminExtensions(appId: string, target: AdminTarget): Promise<AdminExtensionView[]> {
  const { data } = await httpClient.get<AdminExtensionView[]>(`${apps(appId)}/admin-extensions?target=${seg(target)}`);

  return data;
}

/** iframe src (with a 10-minute session token carrying target + resourceId). */
export async function apiCreateAdminExtensionSession(
  appId: string,
  installId: string,
  body: AdminExtensionSessionRequest,
): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>(
    `${apps(appId)}/installs/${seg(installId)}/admin-extensions/session`,
    body,
  );

  return data;
}

// Many resource views mount slots for the same target at once (a review row
// per review): share one list request per app + target for a short while.
const TTL_MS = 60_000;
const cache = new Map<string, { at: number; promise: Promise<AdminExtensionView[]> }>();

export function listAdminExtensionsCached(appId: string, target: AdminTarget): Promise<AdminExtensionView[]> {
  const key = `${appId}\u0000${target}`;
  const hit = cache.get(key);

  if (hit && Date.now() - hit.at < TTL_MS) {
    return hit.promise;
  }

  const promise = apiListAdminExtensions(appId, target).catch((err) => {
    cache.delete(key);
    throw err;
  });

  cache.set(key, { at: Date.now(), promise });

  return promise;
}

/** Forget cached lists (after installing or uninstalling a plugin). */
export function invalidateAdminExtensions(appId?: string): void {
  for (const key of [...cache.keys()]) {
    if (!appId || key.startsWith(`${appId}\u0000`)) {
      cache.delete(key);
    }
  }
}
